<template>
  <VBusHelper
    :reactOn="constants.EVENTS.DELIVERYBOOK.ADD_MODAL_OPEN"
    :handler="showModal"
  />

  <DialogComponent
    ref="modal"
    dialogId="deliverybook-add-dialog"
    type="bottomsheet"
    noInnerPadding
    @closed="resetForm"
  >
    <div class="deliverybook-add-dialog">
      <!-- Header -->
      <div
        class="px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10"
      >
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-xl font-bold text-slate-800">
              Ajouter une adresse
            </h2>
            <p class="text-sm text-slate-500 mt-1">
              Selectionnez la position sur la carte
            </p>
          </div>
          <button
            class="w-10 h-10 flex items-center justify-center rounded-2xl bg-slate-100 hover:bg-slate-200 transition-all duration-300"
            @click="hideModal"
          >
            <svg
              class="w-5 h-5 text-slate-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              stroke-width="2.5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Map Container -->
      <div class="relative h-64 bg-slate-100">
        <button
          class="absolute inset-0 flex flex-col items-center justify-center gap-3 w-full"
          @click="openLocationPicker"
        >
          <div
            class="w-16 h-16 bg-gradient-to-br from-orange-400 to-primary-500 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30"
          >
            <svg
              class="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </div>
          <span class="text-sm font-bold text-slate-600">
            {{
              selectedLocation
                ? 'Modifier la position'
                : 'Selectionner sur la carte'
            }}
          </span>
          <span
            v-if="selectedLocation"
            class="text-xs text-slate-500 max-w-xs text-center"
          >
            {{ selectedLocation.address || 'Position selectionnee' }}
          </span>
        </button>
      </div>

      <!-- Form -->
      <div class="p-6 space-y-4">
        <!-- Name -->
        <div>
          <label class="block text-sm font-semibold text-slate-700 mb-2">
            Nom du contact *
          </label>
          <input
            v-model="form.name"
            type="text"
            placeholder="Ex: Jean Dupont"
            class="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
            :class="{ 'border-red-300': errors.name }"
          />
          <p v-if="errors.name" class="text-red-500 text-xs mt-1">
            {{ errors.name }}
          </p>
        </div>

        <!-- Pseudo (optional) -->
        <div>
          <label class="block text-sm font-semibold text-slate-700 mb-2">
            Pseudo (optionnel)
          </label>
          <input
            v-model="form.pseudo"
            type="text"
            placeholder="Ex: Maman Sami de Godomey"
            class="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
          />
        </div>

        <!-- Phone -->
        <div>
          <label class="block text-sm font-semibold text-slate-700 mb-2">
            Telephone *
          </label>
          <div class="relative">
            <span
              class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold"
            >
              +229
            </span>
            <input
              v-model="form.phoneNumber"
              type="tel"
              placeholder="01 23 45 67 89"
              class="w-full pl-16 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10 transition-all duration-300 text-slate-700 placeholder:text-slate-400"
              :class="{ 'border-red-300': errors.phoneNumber }"
            />
          </div>
          <p v-if="errors.phoneNumber" class="text-red-500 text-xs mt-1">
            {{ errors.phoneNumber }}
          </p>
        </div>

        <!-- Instructions -->
        <div>
          <label class="block text-sm font-semibold text-slate-700 mb-2">
            Instructions (optionnel)
          </label>
          <textarea
            v-model="form.description"
            rows="3"
            placeholder="Ex: A cote du marche, maison bleue..."
            class="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl focus:outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-500/10 transition-all duration-300 text-slate-700 placeholder:text-slate-400 resize-none"
          ></textarea>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="p-6 pt-0">
        <button
          class="w-full py-4 bg-gradient-to-r from-orange-500 to-primary-500 text-white rounded-2xl font-bold shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isSubmitting"
          @click="submitForm"
        >
          <span v-if="!isSubmitting">Enregistrer l'adresse</span>
          <span v-else>Enregistrement...</span>
        </button>
      </div>
    </div>
  </DialogComponent>
</template>

<script setup>
import { ref, reactive, onBeforeUnmount } from 'vue';
import { bus } from '@core/events/eventSystem';
import constants from '@core/utils/constants';

const modal = ref(null);

// State
const isSubmitting = ref(false);
const selectedLocation = ref(null);
const form = reactive({
  name: '',
  pseudo: '',
  phoneNumber: '',
  description: ''
});
const errors = reactive({
  name: '',
  phoneNumber: ''
});

// Methods
function showModal() {
  modal.value?.show();
}

function hideModal() {
  modal.value?.hide();
}

function openLocationPicker() {
  bus.emit(constants.EVENTS.MAP.LOCATION_SELECTION_REQUIRED, {
    requestKey: 'deliverybook_add_address',
    additionalFields: []
  });
}

function validateForm() {
  let isValid = true;

  // Reset errors
  errors.name = '';
  errors.phoneNumber = '';

  // Validate name
  if (!form.name.trim()) {
    errors.name = 'Le nom est requis';
    isValid = false;
  }

  // Validate phone
  if (!form.phoneNumber.trim()) {
    errors.phoneNumber = 'Le telephone est requis';
    isValid = false;
  }

  // Validate location
  if (!selectedLocation.value) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Position requise',
      body: 'Veuillez selectionner une position sur la carte'
    });
    isValid = false;
  }

  return isValid;
}

function submitForm() {
  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;

  const addressData = {
    name: form.name.trim(),
    pseudo: form.pseudo.trim(),
    phoneNumber: form.phoneNumber.trim(),
    description: form.description.trim(),
    lat: selectedLocation.value.lat,
    lng: selectedLocation.value.lng,
    address: selectedLocation.value.address || '',
    city: selectedLocation.value.city || ''
  };

  bus.emit(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATE, addressData);
}

function resetForm() {
  form.name = '';
  form.pseudo = '';
  form.phoneNumber = '';
  form.description = '';
  selectedLocation.value = null;
  errors.name = '';
  errors.phoneNumber = '';
  isSubmitting.value = false;
}

// Listen for location selection from map
function onLocationSelected(data) {
  if (data.requestKey === 'deliverybook_add_address') {
    selectedLocation.value = data.selectedLocation;
  }
}

// Listen for address created to close modal
function onAddressCreated(data) {
  if (data.address) {
    // Reset form and close modal after successful creation
    setTimeout(() => {
      isSubmitting.value = false;
      resetForm();
      hideModal();
    }, 500);
  }
}

// Listen for address create error
function onAddressError(error) {
  console.error('[DeliveryBookAddDialog] Address create error:', error);
  isSubmitting.value = false;
}

onBeforeUnmount(() => {
  bus.off('map_location_selected', onLocationSelected);
  bus.off(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATED, onAddressCreated);
  bus.off(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATE_ERROR, onAddressError);
});

// Register listeners on mount
bus.on('map_location_selected', onLocationSelected);
bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATED, onAddressCreated);
bus.on(constants.EVENTS.DELIVERYBOOK.ADDRESS_CREATE_ERROR, onAddressError);
</script>
